/**
 * 5 · Fine linea — imballaggio e spedizione.
 *
 * **L'operatore decide quando andare**: la lista dice quale carrello e quanti
 * capi, non impone il momento (regola 26). E **le scatole non seguono le
 * cassette**: ci si versa dentro, e la packing list nasce da quello che entra.
 *
 * One page, no tabs: the search on top, the expected orders under it (the
 * ready ones of the flow, then those opened by hand), and **no order opens by
 * itself** — the operator chooses. Once chosen, what changes is only whether
 * the order has crates: the bench lives in `end-of-line/bench.tsx`.
 *
 * La doppia etichettatura è visibile: alla chiusura di ogni scatola esce il
 * barcode provvisorio — quello che si bippa sul PS130 — e solo a spedizione
 * chiusa, contati i colli, escono le etichette col «1 di N».
 */

import { Notifiche } from '../components/notifications';
import { Sezione } from '../components/section';
import { Stato } from '../components/state';
import { Avviso, useFlusso } from '../flow-api';
import { SpedizioneBrt } from './end-of-line/carrier-shipment';
import { Banco, type OrdineDiOggi, type RigaManuale } from './end-of-line/bench';
import { type Cassette, type Lavoro, type Spedizione } from './end-of-line/crate-order';
import { PesoAMano } from './end-of-line/manual-weight';
import { DaSistemare, type VoceDaSistemare } from './end-of-line/to-fix';

interface VistaFineLinea {
  daImballare: Lavoro[];
  spedizioni: Spedizione[];
  // Optional only for views written before BRT: the API always sends it.
  daSistemare?: VoceDaSistemare[];
  cassette: Cassette;
  // Orders opened by hand at the bench. Optional only for views written before it.
  manuali?: RigaManuale[];
  // Every order a box was opened for today. Optional only for views written before it.
  oggi?: OrdineDiOggi[];
}

export function FineLinea() {
  const { dati, caricando, ultimo, inCorso, fai, faiInOrdine } = useFlusso<VistaFineLinea>(
    'fine-linea',
    '/fine-linea',
  );
  if (caricando || !dati) return <Stato quale="caricando" dice="Apro il fine linea…" />;

  const daSistemare = dati.daSistemare ?? [];
  const manuali = dati.manuali ?? [];
  // An order opened by hand says where it stands in «Ordini attesi» («GLS
  // partita» included), and its boxes are weighed by the station alone.
  const aMano = new Set(manuali.map((m) => m.ordine));
  const oggi = dati.oggi ?? [];
  const diOggi = new Set(oggi.map((o) => o.ordine));

  // Under the chosen order, whatever it is: shipments waiting for someone,
  // boxes without a weight, what already left.
  const accanto = (aSchermo: string | null) => {
    // Closed boxes the PS130 never weighed: once the last crate is packed the
    // order leaves the ready list, and the missing weight would have nowhere
    // else to be typed. The order on screen shows them itself.
    const senzaPeso = dati.spedizioni
      .filter((s) => !s.chiusa && s.ordine !== aSchermo && !aMano.has(s.ordine))
      .flatMap((s) =>
        s.colli
          .filter((c) => c.chiuso && c.pesoKg === null)
          .map((c) => ({ ...c, ordine: s.ordine })),
      );
    // Nor is the order on screen listed again: its shipment is right there.
    // Today's are in «Oggi» already.
    const spedite = dati.spedizioni.filter(
      (s) => s.chiusa && !aMano.has(s.ordine) && !diOggi.has(s.ordine) && s.ordine !== aSchermo,
    );

    return (
      <>
        {daSistemare.length > 0 && (
          <Sezione titolo="Da sistemare" distanziata accanto={<>{daSistemare.length}</>}>
            <DaSistemare voci={daSistemare} inCorso={inCorso} fai={fai} faiInOrdine={faiInOrdine} />
          </Sezione>
        )}

        {senzaPeso.length > 0 && (
          <Sezione titolo="Colli senza peso" distanziata>
            {senzaPeso.map((c) => (
              <article key={c.barcode} className="riga riga--part">
                <div className="riga__cliente">ordine {c.ordine}</div>
                <div className="riga__sku">Collo {c.progressivo} · il PS130 non l’ha pesato</div>
                <PesoAMano
                  ordine={c.ordine}
                  progressivo={c.progressivo}
                  barcode={c.barcode}
                  inCorso={inCorso}
                  fai={fai}
                />
              </article>
            ))}
          </Sezione>
        )}

        {spedite.length > 0 && (
          <Sezione titolo="Spedite" distanziata>
            {spedite.map((s) =>
              s.vettore ? (
                <SpedizioneBrt
                  key={s.ordine}
                  ordine={s.ordine}
                  colli={s.colli.length}
                  vettore={s.vettore}
                  inCorso={inCorso}
                  fai={fai}
                />
              ) : (
                <article key={s.ordine} className="riga riga--ok">
                  <div className="riga__cliente">ordine {s.ordine}</div>
                  <div className="riga__sku">
                    {s.corriere} · {s.colli.length} colli · {s.ragioneCorriere}
                  </div>
                </article>
              ),
            )}
          </Sezione>
        )}
      </>
    );
  };

  return (
    <div className="shell">
      <Banco
        attesi={dati.daImballare}
        manuali={manuali}
        spedizioni={dati.spedizioni}
        cassette={dati.cassette}
        inCorso={inCorso}
        fai={fai}
        ultimo={ultimo}
        accanto={accanto}
        inTestata={<Notifiche />}
        oggi={oggi}
      />
      {/* Only refusals: what went well shows on the order and in «Oggi». */}
      <Avviso esito={ultimo?.ok === false ? ultimo : null} />
    </div>
  );
}
